import { useState, useCallback, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { UserFilesBatchingActions, UserFilesBatchedOperationKind, UserFilesBatchedOperationStatus } from '@/user_files/batching/store/slice'
import { kNoSelectionAncestry, pruneCoveredSelection } from '@/user_files/batching/prune-covered-selection'
import { selectActiveUserFilesOperation, selectIsUserFilesOperationActive } from '@/user_files/batching/store/selectors'
import type { FileForSelection, FolderForSelection } from './useDriveSelection'
import type { FileSelectionPrecondition, FolderSelectionPrecondition } from '@/user_files/schemas/preconditions'
import { buildFileSelectionPrecondition, buildFolderSelectionPrecondition } from '@/user_files/utils/preconditions'
import { kDeleteMaxFileIdsPerRequest, kDeleteMaxFolderIdsPerRequest } from '@/user_files/delete/constants'
import { postDeletePreview } from '@/user_files/delete/fetch/post-delete-preview'
import { DeleteSelectionMode, type DeletePreviewResponse } from '@/user_files/delete/schemas'

type FileToDelete = {
  id: string
  name: string
  precondition: FileSelectionPrecondition
}

type FolderToDelete = {
  id: string
  name: string
  precondition: FolderSelectionPrecondition
}

type UseDeleteDialogParams = {
  files: FileForSelection[]
  folders: FolderForSelection[]
  currentFolderId: string | null
  selectedSelectableFileIds: string[]
  selectedFolderIds: string[]
  clearSelection: () => void
  /**
   * Ancestor folder ids per visible row id, present only when the rows are search results.
   * Used to drop items whose containing folder is being deleted too — see `pruneCoveredSelection`.
   */
  ancestorFolderIdsById?: ReadonlyMap<string, readonly string[]>
}

export function useDeleteDialog({
  files,
  folders,
  currentFolderId,
  selectedSelectableFileIds,
  selectedFolderIds,
  clearSelection,
  ancestorFolderIdsById = kNoSelectionAncestry,
}: UseDeleteDialogParams) {
  const dispatch = useAppDispatch()
  const activeOperation = useAppSelector(selectActiveUserFilesOperation)
  const isAnyOperationActive = useAppSelector(selectIsUserFilesOperationActive)

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [filesToDelete, setFilesToDelete] = useState<FileToDelete[]>([])
  const [foldersToDelete, setFoldersToDelete] = useState<FolderToDelete[]>([])
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [preview, setPreview] = useState<DeletePreviewResponse | null>(null)
  // Every open handler sets this true in the same batch as opening the dialog. Leaving it to the
  // effect below would paint one frame with the destructive button enabled before the check starts.
  const [previewLoading, setPreviewLoading] = useState(false)
  // What the user ticked, kept beside the pruned lists above because the two differ and the dialog
  // needs both: it headlines the selection, while the request carries only the top-most items
  // (FE-594 / BB-04). Every open handler sets this in the same batch as opening the dialog.
  const [selectedCounts, setSelectedCounts] = useState({ fileCount: 0, folderCount: 0 })

  useEffect(() => {
    const fileIds = filesToDelete.map((f) => f.id)
    const folderIds = foldersToDelete.map((f) => f.id)

    // The preview endpoint takes one un-batched selection, so a selection larger than its limits
    // skips the warning and falls back to the plain confirmation. Deletion itself still batches.
    const isPreviewable =
      deleteDialogOpen &&
      fileIds.length + folderIds.length > 0 &&
      fileIds.length <= kDeleteMaxFileIdsPerRequest &&
      folderIds.length <= kDeleteMaxFolderIdsPerRequest

    if (!isPreviewable) {
      setPreview(null)
      setPreviewLoading(false)
      return
    }

    let cancelled = false
    setPreviewLoading(true)

    postDeletePreview({ file_ids: fileIds, folder_ids: folderIds })
      .then((result) => {
        if (!cancelled) setPreview(result)
      })
      .catch(() => {
        // Fail open: a pre-flight failure shouldn't block deletion, just skip the severe warning.
        if (!cancelled) setPreview(null)
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [deleteDialogOpen, filesToDelete, foldersToDelete])

  const isDeleting =
    activeOperation?.kind === UserFilesBatchedOperationKind.DeleteSelection &&
    (activeOperation.status === UserFilesBatchedOperationStatus.Queued || activeOperation.status === UserFilesBatchedOperationStatus.Running)

  const openDeleteForSelected = useCallback(() => {
    // A folder delete cascades, so sending an item that lives inside another selected folder would
    // be a redundant second request for something the first one already removed.
    //
    // Pruning stops at the request. The dialog and the completion toast both still count what the
    // user ticked, so ticking a folder and a file inside it reads "Delete 2 items?" and then
    // "2 items deleted" even though only the folder is sent — deleting the folder is what deletes
    // that file (FE-594 / BB-04).
    const pruned = pruneCoveredSelection({ fileIds: selectedSelectableFileIds, folderIds: selectedFolderIds, ancestorFolderIdsById })

    // Sets rather than `Array.includes`: search results are matter-wide, so both the rows and the
    // selection can run to thousands and the nested scan would be quadratic.
    const prunedFileIds = new Set(pruned.fileIds)
    const prunedFolderIds = new Set(pruned.folderIds)

    setFilesToDelete(
      files.filter((f) => prunedFileIds.has(f.id)).map((f) => ({ id: f.id, name: f.original_filename, precondition: buildFileSelectionPrecondition(f) }))
    )
    setFoldersToDelete(
      folders.filter((f) => prunedFolderIds.has(f.id)).map((f) => ({ id: f.id, name: f.name, precondition: buildFolderSelectionPrecondition(f) }))
    )
    setSelectedCounts({ fileCount: selectedSelectableFileIds.length, folderCount: selectedFolderIds.length })
    setPreviewLoading(true)
    setDeleteDialogOpen(true)
  }, [files, folders, selectedSelectableFileIds, selectedFolderIds, ancestorFolderIdsById])

  const openDeleteForFile = useCallback(
    (fileId: string) => {
      const file = files.find((f) => f.id === fileId)
      if (!file) return
      setFilesToDelete([{ id: file.id, name: file.original_filename, precondition: buildFileSelectionPrecondition(file) }])
      setFoldersToDelete([])
      setSelectedCounts({ fileCount: 1, folderCount: 0 })
      setPreviewLoading(true)
      setDeleteDialogOpen(true)
    },
    [files]
  )

  const openDeleteForFolder = useCallback(
    (folderId: string) => {
      const folder = folders.find((f) => f.id === folderId)
      if (!folder) return
      setFilesToDelete([])
      setFoldersToDelete([{ id: folder.id, name: folder.name, precondition: buildFolderSelectionPrecondition(folder) }])
      setSelectedCounts({ fileCount: 0, folderCount: 1 })
      setPreviewLoading(true)
      setDeleteDialogOpen(true)
    },
    [folders]
  )

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false)
    setFilesToDelete([])
    setFoldersToDelete([])
    setSelectedCounts({ fileCount: 0, folderCount: 0 })
    setDeleteError(null)
    setPreviewLoading(false)
  }, [])

  // Both confirm paths send the same selection; `mode` decides whether the backend also deletes
  // the files a Timeline, Medical Chronology, or Medical Billing Summary still uses.
  const enqueueDelete = useCallback(
    (mode: DeleteSelectionMode) => {
      if (!currentFolderId) return
      if (filesToDelete.length === 0 && foldersToDelete.length === 0) return

      if (isAnyOperationActive) {
        setDeleteError('Another operation is in progress. Please wait.')
        return
      }

      setDeleteError(null)
      dispatch(
        UserFilesBatchingActions.enqueueDeleteSelection({
          refreshFolderId: currentFolderId,
          fileIds: filesToDelete.map((f) => f.id),
          folderIds: foldersToDelete.map((f) => f.id),
          filePreconditions: filesToDelete.map((f) => f.precondition),
          folderPreconditions: foldersToDelete.map((f) => f.precondition),
          mode,
          selectedItems: selectedCounts.fileCount + selectedCounts.folderCount,
        })
      )
      clearSelection()
      closeDeleteDialog()
    },
    [currentFolderId, filesToDelete, foldersToDelete, selectedCounts, isAnyOperationActive, dispatch, clearSelection, closeDeleteDialog]
  )

  const confirmDelete = useCallback(() => enqueueDelete(DeleteSelectionMode.All), [enqueueDelete])
  const confirmDeleteNotInUse = useCallback(() => enqueueDelete(DeleteSelectionMode.NotInUse), [enqueueDelete])

  return {
    deleteDialog: {
      open: deleteDialogOpen,
      fileCount: selectedCounts.fileCount,
      folderCount: selectedCounts.folderCount,
      fileIds: filesToDelete.map((file) => file.id),
    },
    openDeleteForSelected,
    openDeleteForFile,
    openDeleteForFolder,
    closeDeleteDialog,
    confirmDelete,
    confirmDeleteNotInUse,
    isDeleting,
    deleteError,
    preview,
    previewLoading,
  }
}
